import { api } from './api';
import { Subscription, Payment, SubscriptionStats } from '../types/subscription';

class AdminSubscriptionService {
  private baseUrl = '/v1/admin';

  // Subscription methods
  async getSubscriptions(params: {
    page?: number;
    per_page?: number;
    status?: string;
    plan_type?: string;
  } = {}): Promise<Subscription[]> {
    const searchParams = new URLSearchParams();

    if (params.page) searchParams.append('page', params.page.toString());
    if (params.per_page) searchParams.append('per_page', params.per_page.toString());
    if (params.status) searchParams.append('status', params.status);
    if (params.plan_type) searchParams.append('plan_type', params.plan_type);

    const response = await api.get(`${this.baseUrl}/subscriptions?${searchParams.toString()}`);
    return response.data;
  }

  async getSubscription(subscriptionId: number): Promise<Subscription> {
    const response = await api.get(`${this.baseUrl}/subscriptions/${subscriptionId}`);
    return response.data;
  }

  async getUserSubscriptions(userId: number): Promise<Subscription[]> {
    const response = await api.get(`${this.baseUrl}/users/${userId}/subscriptions`);
    return response.data;
  }

  // Payment methods
  async getPayments(params: {
    page?: number;
    per_page?: number;
    status?: string;
    user_id?: number;
  } = {}): Promise<Payment[]> {
    const searchParams = new URLSearchParams();

    if (params.page) searchParams.append('page', params.page.toString());
    if (params.per_page) searchParams.append('per_page', params.per_page.toString());
    if (params.status) searchParams.append('status', params.status);
    if (params.user_id) searchParams.append('user_id', params.user_id.toString());

    const response = await api.get(`${this.baseUrl}/payments?${searchParams.toString()}`);
    return response.data;
  }

  async getPayment(paymentId: number): Promise<Payment> {
    const response = await api.get(`${this.baseUrl}/payments/${paymentId}`);
    return response.data;
  }

  async getUserPayments(userId: number): Promise<Payment[]> {
    const response = await api.get(`${this.baseUrl}/users/${userId}/payments`);
    return response.data;
  }

  // Statistics
  async getSubscriptionStats(): Promise<SubscriptionStats> {
    const response = await api.get(`${this.baseUrl}/subscription-stats`);
    return response.data;
  }

  // Helper methods
  getPlanDisplayName(planType: string): string {
    switch (planType) {
      case 'essential':
        return 'Essential ($799/year)';
      case 'gold':
        return 'Gold ($1,999/year)';
      case 'premium':
        return 'Premium ($3,999/year)';
      default:
        return planType;
    }
  }

  getPlanBadgeColor(planType: string): string {
    switch (planType) {
      case 'essential':
        return 'bg-blue-100 text-blue-800';
      case 'gold':
        return 'bg-yellow-100 text-yellow-800';
      case 'premium':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getStatusDisplayName(status: string): string {
    switch (status) {
      case 'active':
        return 'Active';
      case 'canceled':
        return 'Canceled';
      case 'past_due':
        return 'Past Due';
      case 'unpaid':
        return 'Unpaid';
      default:
        return status;
    }
  }

  getStatusBadgeColor(status: string): string {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'canceled':
        return 'bg-red-100 text-red-800';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-800';
      case 'unpaid':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getPaymentStatusBadgeColor(status: string): string {
    switch (status) {
      case 'succeeded':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'canceled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  formatCurrency(amount: number, currency: string = 'usd'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  isSubscriptionActive(subscription: Subscription): boolean {
    return subscription.status === 'active';
  }

  getDaysUntilRenewal(subscription: Subscription): number | null {
    if (!subscription.current_period_end) return null;
    const endDate = new Date(subscription.current_period_end);
    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  }
}

export const adminSubscriptionService = new AdminSubscriptionService();
